import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import capitalize from 'lodash/capitalize';
import { MouseEvent, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import GroupItem from './GroupItem';
import styles from './GroupSelector.module.scss';
import Item from './Item';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

export interface GroupOption<T, G> {
    id: T;
    label: string;
    groupId: G;
    groupLabel: string;
}

interface GroupSelectorProps<T, G> {
    options: Array<GroupOption<T, G>>;
    selected?: GroupOption<T, G>;
    placeholder: string;
    className?: string;
    error?: boolean;
    onSelect?: (option: GroupOption<T, G>) => void;
}

export const GroupSelector = <T, G>(props: GroupSelectorProps<T, G>) => {
    const [expanded, setExpanded] = useState(false);

    const clickHeaderHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setExpanded(prev => !prev);
    };

    const clickItemHandler = (option: GroupOption<T, G>) => {
        setExpanded(false);
        props.onSelect?.(option);
    };

    const groups = props.options.reduce<Array<{ groupId: G; groupLabel: string; options: Array<GroupOption<T, G>> }>>(
        (result, option) => {
            const group = result.find(g => g.groupId === option.groupId);

            if (!group) {
                result.push({ groupId: option.groupId, groupLabel: option.groupLabel, options: [option] });
            } else {
                group.options.push(option);
            }

            return result;
        },
        []
    );

    return (
        <div
            className={clsx(
                styles.selectorContainer,
                { [styles.expandedSelectorContainer]: expanded },
                { [styles.error]: props.error },
                props.className
            )}
        >
            <ClickAwayListener onClickAway={() => setExpanded(false)}>
                <div>
                    <div
                        className={styles.header}
                        onClick={clickHeaderHandler}
                    >
                        <div className={styles.headerLeft}>
                            <span className={clsx(styles.label, { [styles.expandedLabel]: expanded })}>
                                {props.selected
                                    ? `(${capitalize(props.selected.groupLabel.slice(0, -1))}) ${props.selected.label}`
                                    : props.placeholder}
                            </span>
                        </div>
                        <div className={styles.headerRight}>
                            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                        </div>
                    </div>
                    {expanded && (
                        <div className={styles.list}>
                            <>
                                <div className={styles.listHeader}>
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        className={clsx(styles.closeIcon, { [styles.error]: props.error })}
                                        onClick={() => setExpanded(false)}
                                    />
                                </div>
                                {groups.map(g => (
                                    <>
                                        <GroupItem
                                            key={`${g.groupId}`}
                                            label={g.groupLabel}
                                        />
                                        {g.options.map(o => (
                                            <Item
                                                key={`${o.groupId}_${o.id}`}
                                                label={o.label}
                                                onClick={() => clickItemHandler(o)}
                                            />
                                        ))}
                                    </>
                                ))}
                            </>
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </div>
    );
};

export default GroupSelector;
